import React from "react";

import { Title } from "~/components";
import { usePageTitle } from "~/shared";

export const TermsOfUse = () => {
  usePageTitle("Terms of Use");

  return (
    <main className="min-h-screen bg-gray-50 py-4 md:py-8">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <div className="mb-6 text-center">
          <Title className="mb-2 text-2xl font-bold text-gray-900 md:text-3xl">
            Elektra Health Terms of Use
          </Title>
          <p className="text-sm text-gray-500">
            <strong>Last modified:</strong> August 22, 2025
          </p>
        </div>

        <div className="space-y-8">
          {/* Introduction Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              Introduction
            </h2>
            <p className="mb-4 text-gray-700">
              These Terms of Use (or "TOU") are an agreement between you and My
              Elektra, Inc., a Delaware corporation ("Elektra Health"). By
              clicking on this box, you agree to the following terms and
              conditions, including the Privacy Policy, when: (i) using our
              website https://www.elektrahealth.com (the "Site") and the Elektra
              Health platform and mobile applications managed by Mighty
              Software, Inc. ("Mighty Networks"), as well as the services
              ("Services"), information and content ("Content"), and programs
              ("Programs") available to users through the Site.; and (ii)
              accessing medical consultations facilitated by Elektra Health when
              conducted in person or via smartphone app, web-based communication
              or via telephone by a licensed practitioner (collectively,
              "Services").
            </p>
            <p className="mb-4 text-gray-700">
              These TOU may change. Because your use of Elektra Health means
              that you agree to these TOU, if we revise and update these TOU,
              your continued use of Elektra Health, this Site and the Services
              will mean that you accept those changes. You choose whether or not
              you use Elektra Health. If you do not agree to all of these TOU,
              please do not use Elektra Health, this Site and the Services.
            </p>
          </section>

          {/* Contact Information Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              Elektra Business Contact Information
            </h2>
            <ul className="mb-4 ml-6 list-disc text-gray-700">
              <li>Email: hello@elektrahealth.com</li>
              <li>Phone: (646) 760-6669</li>
              <li>
                Mailing address: My Elektra, Inc. 228 Park Avenue S. PMB 49409
                New York, NY 10003-1502 United States
              </li>
              <li>Physician contact email: care@elektrahealth.com</li>
            </ul>
          </section>

          {/* Medical Emergency Warning */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              Medical Emergency Warning
            </h2>
            <p className="text-gray-700">
              IF YOU HAVE, OR ARE HAVING, A MEDICAL EMERGENCY, YOU SHOULD
              CONTACT YOUR PHYSICIAN OR CALL 911, OR GO TO YOUR LOCAL EMERGENCY
              ROOM.
            </p>
          </section>

          {/* Medical Information Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              Medical Information and Content
            </h2>
            <p className="mb-4 font-medium text-gray-700">
              Please note that certain sections of the Site may be intended only
              for particular audiences such as health care professionals,
              patients and/or the general public. All Content including images,
              text documents, audio, video, and interactive media published on
              the Site is presented in summary form and is provided for
              informational purposes only. The Content is intended to help
              patients and medical providers better understand certain health
              conditions and treatment options. The Content is not intended in
              any way to be a substitute for professional medical advice and
              should not be interpreted as treatment recommendations. Only a
              qualified health provider who has had an opportunity to interact
              with the patient directly, with access to the patient's records
              and the opportunity to conduct appropriate follow-up, can provide
              recommendations for treatment.
            </p>
            <p className="mb-4 font-medium text-gray-700">
              Always seek the advice of your qualified health provider with any
              questions you may have regarding a medical condition or
              prescription. Neither the Content nor any other service offered by
              or through the Site is intended to be relied on for medical
              diagnosis or treatment without a qualified healthcare provider's
              interaction and involvement. Never disregard medical advice or
              delay in seeking advice or treatment because of something you have
              read on the Site.
            </p>
            <p className="mb-4 text-gray-700">
              Neither Elektra Health, nor any of its subsidiaries or affiliates
              or any third party who may promote the Site or Service or provide
              a link to the Service, shall be liable for any information or
              advice obtained via the Site or Service, nor any information
              obtained on the Site. Elektra Health does not recommend or endorse
              any specific tests, medications, products, or procedures. You
              acknowledge that your reliance on any information delivered via
              the Site or Service is solely at your own risk and you assume full
              responsibility for all risks associated herewith.
            </p>
          </section>

          {/* Arbitration Notice */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              Arbitration Notice
            </h2>
            <p className="text-gray-700">
              YOU AGREE THAT DISPUTES BETWEEN YOU AND ELEKTRA HEALTH WILL BE
              RESOLVED BY BINDING, INDIVIDUAL ARBITRATION AND YOU WAIVE YOUR
              RIGHTS TO A JURY TRIAL AND TO PARTICIPATE IN A CLASS ACTION
              LAWSUIT OR CLASS-WIDE ARBITRATION, AS FURTHER SET FORTH BELOW.
            </p>
          </section>

          {/* General Terms Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              General
            </h2>
            <p className="mb-4 text-gray-700">
              Prior to accessing the Services, you represent and warrant that
              you are at least eighteen years of age and possess the legal right
              and ability, to: (i) agree to these TOU; (ii) register for the
              Services under your own name; and provide true, accurate, current,
              and complete information on the registration form and to keep this
              information current and updated as needed; and (iii) use the
              Services in accordance with these TOU and abide by the obligations
              hereunder. You agree that you can only access the Services in
              those states where Elektra Health is currently offering access to
              the Services.
            </p>
          </section>

          {/* UHC Store Access Section */}
          <section className="rounded-lg border border-gray-300 bg-gray-50 p-6">
            <h2 className="mb-4 text-xl font-semibold italic text-gray-900">
              If you are accessing Elektra through the UnitedHealthcare Store:
            </h2>
            <p className="italic text-gray-700">
              Please note that all questions related to billing, subscription
              renewal, or cancellation must be directed to UnitedHealthcare's
              Member Support team. Elektra cannot process subscription changes
              for members who enrolled via the UHC Store. Please contact UHC via
              https://member.uhc.com/myuhc or through the information on your
              card.
            </p>
          </section>

          {/* Online Questionnaire Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              Elektra Health Online Questionnaire
            </h2>
            <p className="mb-4 text-gray-700">
              You agree to: (i) fully, accurately and truthfully complete your
              Elektra Health Online Questionnaire or Intake Form; and (ii)
              prohibit anyone else from using your Elektra Health account
              employing information from your Elektra Health Online
              Questionnaire. You agree to provide accurate, current and complete
              information about yourself for your Elektra Health Online
              Questionnaire, and to periodically review and to update such
              information as needed to keep it accurate, current and complete.
              You agree to immediately notify Elektra Health of any actual or
              suspected unauthorized use of your Elektra Health account, profile
              or credentials or other security concerns of which you become
              aware.
            </p>
          </section>

          {/* Practitioner Relationship Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              Relationship with Practitioner
            </h2>
            <p className="mb-4 text-gray-700">
              If you utilize telemedicine services, you agree that you are
              entering into an agreement with Electra Health Medical, P.C.,
              Electra Health Medical Mass, P.C., Electra Women's Health Medical
              NJ, P.C., and/or Elektra Women's Health Medical, Inc.
              (collectively, the "Practice") which shall be a provider of
              professional medical services to you, which means, among other
              things, you are entering into a practitioner – patient
              relationship with the physician or licensed practitioner
              associated with the PC (collectively, the "Practitioners") that
              personally performs the Services. You understand and agree that
              Elektra Health is the provider of certain administrative services
              to the Practice and provides access to a technology platform in
              order to facilitate the Services between you and the Practice and
              the Practitioners. You acknowledge that Elektra Health does not
              provide professional medical services itself and the Elektra
              Health website is for informational purposes only. Elektra Health
              does not provide medical advice, diagnosis, or treatment.
            </p>
          </section>

          {/* Telehealth Consent Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              Consent to Treatment Via Telehealth Services
            </h2>
            <p className="mb-4 text-gray-700">
              You agree and consent to receive the Services provided by the
              Practitioners. In connection with the Services, you understand,
              acknowledge and agree to the following:
            </p>
            <p className="mb-4 text-gray-700">
              As a patient, you have the right to be informed about your
              condition and recommended care. This disclosure is to help you
              become better informed so that you may make the decision to give
              or withhold consent as to whether or not to undergo care having
              had the opportunity to discuss potential benefits and risks
              involved.
            </p>
            <p className="mb-4 text-gray-700">
              You consent to the treatment and care constituting the Services
              wherever rendered by the Practitioner (in office or via
              telehealth) and you intend for such consent to remain effective
              after a diagnosis by the Practitioner is made and treatment is
              recommended.
            </p>
            <p className="mb-4 text-gray-700">
              You hereby request and voluntarily consent to virtual examination
              and holistic care with treatment recommendations that may include
              a wide range of prescription, and over-the-counter medicines,
              including hormone replacement therapy, as appropriate
              (collectively, "Medications"), and vitamins, minerals and herbal
              remedies (collectively, "Supplements"), as recommended by the
              licensed medical doctors (M.D.), doctors of osteopathy (D. O.),
              naturopathic doctors (N.D.) and advanced practice professionals,
              including nurse practitioners (N.P.), and other appropriately
              trained and licensed personnel (collectively, "Providers") working
              through the practices with which My Elektra, Inc. contracts,
              including but not limited to Electra Health Medical, P.C., Electra
              Health Medical Mass, P.C., Electra Women's Health Medical NJ,
              P.C., and/or Elektra Women's Health Medical, Inc. (collectively,
              the "Practice") each of which provides services through My
              Elektra, Inc.
            </p>
            <p className="mb-4 text-gray-700">
              The scope of care you receive from the Practitioner will be at the
              sole discretion of the Practitioner with no guarantee of
              diagnosis, treatment or prescription. The Practitioner will
              determine whether or not the condition being diagnosed and/or
              treated is appropriate for the Services. The Practitioner may
              decline to treat you with the Services if, based on the
              professional judgment of the Practitioner, you are not well-suited
              for the Services.
            </p>
            <p className="mb-4 text-gray-700">
              Elektra Health is permitted to release any and all of your
              information, including, without limitation, PHI as defined under
              HIPAA, regarding the Services, consistent with these TOU and the
              Privacy Policy.
            </p>
            <p className="mb-4 text-gray-700">
              There are risks associated with the Services, including, but not
              limited to, the following: 1) loss of records from failure of
              electronic equipment; 2) power failures with loss of
              communication; and 3) invasion of electronic records by outsiders
              (hackers), understanding that it is impossible to list every
              possible risk.
            </p>
            <p className="mb-4 text-gray-700">
              You have the right to withhold or withdraw your consent to the
              Services at any time without affecting your right to future care
              or treatment. You have the right to discuss any treatment plan
              with the Practitioner, including the purpose, potential risks and
              benefits of any test, treatment or care plan.
            </p>
            <p className="mb-4 text-gray-700">
              You have the right to request a copy of all medical information
              transmitted during the provision of the Services, which is
              described in more detail in the Privacy Policy.
            </p>
            <p className="mb-4 text-gray-700">
              You understand that, by having your consent to the Services, the
              Practitioner associated with the Practice may communicate medical
              information concerning you to physicians and other health care
              practitioners located in other parts of the state/jurisdiction or
              outside the state/jurisdiction.
            </p>
          </section>

          {/* Treatment Acknowledgment Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              By acknowledging my consent to these Terms of Use, I understand
              and agree to the following:
            </h2>
            <p className="mb-4 text-gray-700">
              I acknowledge and agree that at any time throughout my course of
              treatment, I can request further explanation of any Medications
              and/or Supplements (collectively, "Therapies"), other methods of
              treatment, and information about the material risks of the
              recommended Therapies and other treatments.
            </p>
            <p className="mb-4 text-gray-700">
              I understand that the U.S. Food and Drug Administration does not
              fully evaluate or approve some Supplements that may be recommended
              in my care, and that some Medications may be prescribed beyond FDA
              approved indications. I understand that, as with Medications,
              Supplements may be associated with side effects in certain
              sensitive individuals, may interact with certain Medications, or
              may cause symptoms due to certain pre-existing disease conditions.
              I do not expect my Providers to be able to anticipate and explain
              all risks and complications, and I wish to rely on my Providers to
              exercise judgment in recommending the Therapies and other
              treatments that my Providers feel at the time, based on the facts
              then known, are in my best interest. I understand that if I do not
              undertake the Therapies and other treatments as recommended, I may
              not get the desired result or may increase chances for an adverse
              effect.
            </p>
            <p className="mb-4 text-gray-700">
              I understand that it is my responsibility to keep my Providers
              up-to-date with all of the Medications, Supplements, and other
              drugs or other remedies that I currently am taking, so that my
              Providers can make the best informed recommendations for my care.
            </p>
            <p className="mb-4 text-gray-700">
              I have the opportunity to ask questions and discuss with my
              Providers to my satisfaction about:
            </p>
            <ul className="mb-4 ml-6 list-disc text-gray-700">
              <li>my suspected diagnosis or condition</li>
              <li>
                the nature, purpose, and potential benefit of the recommended
                Therapies and other treatments
              </li>
              <li>
                the inherent risks, complications, potential hazards, or side
                effects of the recommended Therapies and other treatments
              </li>
              <li>the probability or likelihood of success</li>
              <li>
                reasonable available alternatives to the recommended Therapies
                and other treatments
              </li>
              <li>
                the possible consequences if the recommended Therapies and other
                treatments are not followed and/or nothing is done
              </li>
            </ul>
            <p className="mb-4 text-gray-700">
              I further acknowledge that no guarantees or assurances have been
              made to me concerning the results intended from any recommended
              Therapies or other treatment.
            </p>
            <p className="mb-4 text-gray-700">
              I understand that the Providers of the Practice have been trained
              in a diverse range of diagnostic and therapeutic options, and
              offer treatment using evidence-based medicine, while including
              holistic principles. As such, they may: (a) recommend different
              tests, (b) interpret standard tests differently, and (c) propose
              different Therapies and other treatments than my other providers,
              as many perspectives exist in medicine, and in some cases there
              may be disagreements among qualified medical experts.
            </p>
          </section>

          {/* Telehealth Services Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              Telehealth Services
            </h2>
            <p className="mb-4 text-gray-700">
              I understand that the Providers will provide services to me via
              telehealth. Telehealth is a mode of delivering health care
              services via communication technologies (e.g., internet or cell
              phone) to facilitate diagnosis, consultation, treatment,
              education, care management, and self-management of my care.
            </p>
            <p className="mb-4 text-gray-700">
              With respect to Telehealth services:
            </p>
            <ul className="mb-4 ml-6 list-disc text-gray-700">
              <li>
                I understand that the Practice offers Telehealth consultations,
                which are conducted through videoconferencing technology and my
                Provider will not be present in the room with me.
              </li>
              <li>
                I understand there are potential risks to the use of Telehealth
                technology, including but not limited to, interruptions, delays,
                unauthorized access, and or other technical difficulties. I
                understand that either my Providers or I can discontinue the
                Telehealth appointment if the technical connections are not
                adequate for my visit.
              </li>
              <li>
                I understand that I could seek an in-office visit for my care
                elsewhere, and I am choosing to participate in a Telehealth
                consultation with a Practice Provider.
              </li>
              <li>
                To protect the confidentiality of my health information, I agree
                to undertake my Telehealth consultation in a private location,
                and I understand that my Provider will similarly be in a private
                location.
              </li>
              <li>
                I understand that my healthcare information may be shared by my
                Provider with others for treatment, scheduling or billing
                purposes, and as otherwise set forth in the Practice's Notice of
                Privacy Practices.
              </li>
              <li>
                I understand that my Telehealth visit may be videotaped and
                reviewed for quality purposes.
              </li>
              <li>
                I understand I may have to check with my insurance plan to see
                if telehealth visits are covered.
              </li>
              <li>
                I further understand that I am responsible for payment of any
                amounts due and owing resulting from my Telehealth visit,
                including but not limited to co-pays, deductibles and/or amounts
                not covered by my health plan or any third-party payor.
              </li>
              <li>
                In an emergent situation, I understand that the responsibility
                of my Provider may be to direct me to emergency medical
                services, such as an emergency room.
              </li>
            </ul>
            <p className="text-gray-700">
              I understand that my while the Providers may communicate with me
              through the Practice's secure portal or electronic medical record,
              I also understand the Providers may contact me directly via any
              means of communication for which I provide contact information,
              including but not limited to email and text, and such messages may
              contain the results, reports, instructions and/or advice related
              to my diagnosis and/or treatment. I acknowledge that I am
              responsible for checking and responding to these messages, and I
              may not hold My Elektra, Inc., the Practice, or any Provider
              liable for any injury, loss or claims resulting from my failure to
              read or respond to these messages or comply with the advice or
              instructions contained in a message from the Practice or any
              Provider. I acknowledge that email and text may not be secure
              means of communication, and that any such communications may be
              subject to intercept or other unwanted access or disclosure. Risks
              notwithstanding, I consent to the use of unsecure means of
              communications.
            </p>
          </section>

          {/* Arbitration Agreement Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              Arbitration Agreement
            </h2>
            <p className="mb-4 text-gray-700">
              TO THE EXTENT PERMITTED BY LAW, BY SIGNING THIS AGREEMENT, I AGREE
              THAT MY RIGHT TO TRIAL BY A JURY OR A JUDGE IN A COURT WILL BE
              BARRED AS TO ANY DISPUTE RELATING TO INJURIES THAT MAY RESULT FROM
              NEGLIGENCE DURING MY CARE BY THE PRACTICE AND ITS PROVIDERS, AND
              WILL BE REPLACED BY AN ARBITRATION PROCEDURE.
            </p>
            <p className="text-gray-700">
              I FURTHER AGREE THAT ANY CLAIMS THAT MAY ARISE OUT OF MY CARE WILL
              BE SUBMITTED TO AN ARBITRATOR, RATHER THAN TO A COURT FOR
              DETERMINATION. THE DECISION OF THE ARBITRATION PANEL WILL BE
              FINAL.
            </p>
          </section>

          {/* Consent Form Acknowledgment Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              Acknowledgment of Consent
            </h2>
            <p className="text-gray-700">
              By accepting this form, I acknowledge I have carefully read, or
              have had read to me, and understand the above consent. I give my
              permission and consent to care, including Therapies and other
              treatments by the Practice and its Providers, as well as the
              videotaping of my visit, and I am fully aware of what I am
              signing. I intend this consent form to cover the entire course of
              care for my present condition and for any future condition(s) for
              which I seek Therapies or other treatment from the Practice.
            </p>
          </section>

          {/* Privacy Policy Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              Privacy Policy
            </h2>
            <p className="text-gray-700">
              You agree to Elektra Health's Privacy Policy, the terms of which
              are incorporated herein by reference.
            </p>
          </section>

          {/* Medical Records Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              Medical Records
            </h2>
            <p className="text-gray-700">
              You agree to the entry of your medical records into Elektra
              Health's and the Practice's computer database and understand that
              reasonable measures have been taken to safeguard your medical
              information, in accordance with federal HIPAA standards, as
              amended to date, but no computer or phone system is totally
              secure. Elektra Health recognizes your privacy and, in accordance
              with Elektra Health's Privacy Policy, will not release information
              to anyone without your written authorization or as required or
              permitted by law.
            </p>
          </section>

          {/* Communications Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              Communications
            </h2>
            <p className="mb-4 text-gray-700">
              You understand and agree that the Services involve the
              communication of your medical information, both via text or
              orally, to Practitioners located in other parts of the
              state/jurisdiction or outside of the state/jurisdiction. By
              accepting these TOU, you acknowledge and agree that, Elektra
              Health, the Practice and/or Practitioners may send you messages by
              SMS and/or through the electronic medical record, and emails
              regarding your diagnosis and/or treatment as well as information
              regarding perimenopause and menopause and general women's health.
            </p>
            <p className="mb-4 text-gray-700">
              You further understand and agree that it is your sole
              responsibility to monitor and respond to these messages, reports,
              and emails and that neither Elektra Health nor the Practice nor
              any Practitioner will be responsible in any way and you will not
              hold Elektra Health, the Practice or any Practitioner liable for
              any loss, injury, or claims of any kind resulting from your
              failure to read or respond to these messages or for your failure
              to comply with any treatment recommendations or instructions from
              the Practice or any Practitioner. You agree that all agreements,
              notices, disclosures and other communications that Elektra Health
              and the Practice provide to you electronically satisfy any legal
              requirement that such communications be in writing. You further
              agree that any notices provided electronically are deemed to be
              given and received on the date either Elektra Health and/or the
              Practice transmits any such electronic communication as described
              in these TOU.
            </p>
            <p className="text-gray-700">
              Please note that if you send us an email, the email will not
              necessarily be read or responded to by a Practitioner.
            </p>
          </section>

          {/* Audio and Video Recording Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              Consent to Audio and Video Recording
            </h2>
            <p className="text-gray-700">
              Elektra Health may record (audio and video) all or part of your
              non-medical interaction with us ("Recordings"). We will always ask
              you for consent to record you before we do so. Such Recordings are
              voluntary and may be publicly displayed to other users of the
              Site, used for quality assurance purposes, to better deliver
              Services to you, and to help Elektra Health improve the Site. By
              accessing and using the Site, you agree and consent to such
              Recordings for the purposes and uses set forth in these Terms of
              Use and as otherwise set forth in the Privacy Policy.
            </p>
          </section>

          {/* Adverse Technical Events Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              Adverse Technical Events
            </h2>
            <p className="text-gray-700">
              All information is transmitted over a medium which is beyond our
              control and jurisdiction. Accordingly, Elektra Health assumes no
              responsibility for, or relating to, delay, failure, interruption
              or corruption of any data or other information transmitted in
              connection with use of this Site or sites accessed through this
              site.
            </p>
          </section>

          {/* Authorization for Assignment of Benefits Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              Authorization for Assignment of Benefits
            </h2>
            <p className="mb-4 text-gray-700">
              If you are entitled to benefits under any insurance policy or
              other health benefit plan ("Health Plan"), in consideration of
              your receipt of the Services, you irrevocably assign, transfer and
              convey all rights and benefits payable under the Health Plan for
              Services rendered by Elektra Health and the Practice (as such term
              is hereinafter defined). You agree to cooperate with any efforts
              by Elektra Health to secure reimbursement for the Services
              provided. Further, you designate Elektra Health as your authorized
              representative. By this assignment and designation, you authorize
              payment to be made directly to Elektra Health and the Practice.
              You understand that this authorization and designation does not
              relieve you of financial responsibility for charges incurred by
              you. If your Health Plan sends to you payments for the Services,
              you are to immediately send those payments to Elektra Health. If
              you fail to do so, you will be responsible for those amounts, in
              full, as well as any associated cost-share, deductible, co-pay
              and/or co-insurance. In the event you overpay for the Services,
              you authorize Elektra Health to apply such overpayment to satisfy
              any outstanding charges you owe for the Services. This
              authorization does not include Health Plan payments made on your
              behalf. You further authorize and irrevocably assign to Elektra
              Health the following rights:
            </p>
            <ol className="mb-4 ml-6 list-[lower-roman] space-y-2 text-gray-700">
              <li className="pl-2">
                To communicate with your Health Plan, to request any adjustment
                to your Health Plan's reimbursement of the Services provided,
                and to file any and all necessary claims, demands or appeals
                with your Health Plan arising from a denied, underpaid or
                misclassified claim;
              </li>
              <li className="pl-2">
                To demand and receive the production of, or access to, any
                documents and information, including, without limitation, any
                copies of Health Plan documents, coverage policies, guidelines
                and any other materials affecting the coverage and reimbursement
                of any Services provided to you, from any entity or person to
                the fullest extent of your rights to do so under applicable law;
              </li>
              <li className="pl-2">
                To bring legal action, if needed, in any forum against your
                Health Plan under applicable laws, including, without
                limitation, the Employee Retirement Income Security Act of 1974,
                as amended ("ERISA") and/or the Federal Employee Health Benefit
                Act, as amended;
              </li>
              <li className="pl-2">
                To recover benefits under the terms of your Health Plan, to
                enforce your rights under the terms of your Health Plan, or to
                clarify your rights to future benefits under the terms of your
                Health Plan;
              </li>
              <li className="pl-2">
                To enjoin any act or practice which violates any provision of
                ERISA or the terms of your Health Plan, or to obtain other
                appropriate equitable relief to redress such violations or to
                enforce any provisions of ERISA or the terms of your Health
                Plan; and
              </li>
              <li className="pl-2">
                To recover the costs of pursuing such action, including,
                reasonable attorney fees, as permitted.
              </li>
            </ol>
            <p className="text-gray-700">
              The foregoing designation and assignment of benefits and rights
              are without limitation and without reservation of any part or
              aspect thereof.
            </p>
          </section>

          {/* Payment Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              Payment
            </h2>
            <p className="mb-4 text-gray-700">
              You agree to provide current, complete and accurate purchase and
              account information, including, as applicable, health plan or
              governmental or other third-party payor information, for all
              purchases made through the Site. In addition, you agree that you
              will provide complete and accurate credit card information, which
              will be charged for all co-pay and deductible amounts if the
              Services provided through the Site are covered by your health plan
              or other third-party payor, and all amounts due and payable if you
              do not have such coverage. You agree to promptly update your
              account and other information, including your email address and
              credit card numbers and expiration dates, so that we can complete
              your transactions and contact you as needed. You will be
              responsible for any applicable sales, use, duty, customs or other
              governmental taxes, levies or fees due with respect to your
              purchase of Services. If you make an appointment with a Provider,
              we may charge you if you cancel that appointment without 24 hours
              advance notice.
            </p>
            <p className="mb-4 text-gray-700">
              Our Practice is enrolled with and may act as a participating
              provider in certain federal or state healthcare programs (i.e.,
              Medicare, Medicaid), commercial or employer-sponsored health
              plans, including Medicare advantage plans. To the extent the Site
              indicates that the Providers are enrolled in such programs and the
              Services they provide to you through the Site are "covered
              services" under such programs, you will be required to pay any
              co-pay or deductible amount as required by such programs, and
              Elektra will facilitate collection of amounts paid for such
              covered services on behalf of the Providers.
            </p>
            <p className="mb-4 text-gray-700">
              By agreeing to use the Site, you acknowledge and agree that you
              have the financial responsibility for all Services provided to
              you, including co-pays and deductibles required by Medicare,
              Medicaid or other health plans. In addition, you acknowledge that
              you are responsible for payment in full for any non-covered
              service under Medicare, Medicaid or other health plan.
            </p>
            <p className="mb-4 text-gray-700">
              If you have any questions, please contact us at
              billing@elektrahealth.com.
            </p>
            <p className="mb-4 text-gray-700">
              In connection with any payment transaction, you may be asked to
              supply certain information relevant to the transaction, including,
              without limitation, your credit card number and expiration date,
              your billing address, your shipping address, your phone number
              and/or your email address. By submitting such information, you
              grant Elektra Health without charge the irrevocable, unencumbered,
              universe-wide and perpetual right to provide such information to
              third parties (e.g., payment processing companies) for the purpose
              of facilitating the transaction.
            </p>
            <p className="text-gray-700">
              All credit card, debit card and other monetary transactions on or
              through Elektra Health occur through an online payment processing
              application(s). This online payment processing application(s) is
              provided by Elektra Health's third-party online payment processing
              vendor, Elavon ("Elavon"). Additional information about Elavon,
              its privacy policy and its information security measures
              (collectively, the "Elavon Policies") should be available on the
              Elavon website located at
              https://www.elavon.com/privacy-policy.html or by contacting Elavon
              directly. Reference is made to the Elavon Policies for
              informational purposes only and are in no way incorporated into or
              made a part of these TOU. Elektra Health's relationship with
              Elavon, if any, is merely contractual in nature, as Elavon is
              nothing more than a third-party vendor to Elektra Health, and is
              in no way subject to Elektra Health's direction or control; thus,
              their relationship is not, and should not be construed as, one of
              fiduciaries, franchisors-franchisees, agents-principals,
              employers-employees, partners, joint venturers or the like.
            </p>
          </section>

          {/* Availability Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              Availability
            </h2>
            <p className="text-gray-700">
              You are responsible for providing and maintaining, at your own
              risk, option, and expense, appropriate software and hardware
              capabilities (consistent with any technical, quality or other
              requirements described on the Site) to enable use of the Site,
              including, but not limited to, a computer or mobile device with
              internet access. You are solely responsible for any fees,
              including Internet connection or mobile fees that you incur while
              using the Site. We reserve the right to change the access
              configuration, including any software, hardware, or other
              requirements of the Site at any time without prior notice.
            </p>
          </section>

          {/* Not for Use Outside the US Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              Not for Use Outside the United States
            </h2>
            <p className="text-gray-700">
              Elektra Health makes no claims that materials on the Site are
              appropriate or may be downloaded for use in locations outside the
              United States. Access to the Site from countries or territories
              where such access is illegal is prohibited. Furthermore, our
              databases are located in the United States. If you access this
              website from outside the United States, you do so at your own
              risk. By sending us your data, you consent to its transfer to and
              storage within the United States. Those who access the Site from
              outside the United States do so on their own initiative and are
              responsible for compliance with local laws, rules and regulations.
            </p>
          </section>

          {/* Copyright and Trademarks Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              Copyright and Trademarks
            </h2>
            <p className="text-gray-700">
              All content included on the Site including, but not limited to,
              text, photographs, video, documents, graphics, button icons,
              images, artwork, names, logos, trademarks, service marks and data
              (the "Content"), in any form including the compilation thereof,
              are proprietary to Elektra Health and protected by U.S. and
              international copyright law and conventions. The Content includes
              both Content owned or controlled by Elektra Health and Content
              owned or controlled by third parties and licensed to Elektra
              Health. Subject to these TOU, Elektra Health grants you a limited
              non-transferable, revocable license to access and use the Site for
              your personal use.
            </p>
          </section>

          {/* Links to Other Websites Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              Links to Other Websites
            </h2>
            <p className="text-gray-700">
              Elektra Health may provide links to third-party web sites. Elektra
              Health does not recommend and does not endorse the content on any
              third-party websites. Elektra Health is not responsible for the
              content of linked third-party sites, sites framed within the Site,
              third-party sites provided as search results, or third-party
              advertisements. Your use of third-party websites is at your own
              risk, and subject to the terms and conditions of use for such
              sites. Elektra Health does not endorse any product, service, or
              treatment advertised on the Site.
            </p>
          </section>

          {/* Third Party Products and Services Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              Third Party Products and Services and Elektra Health
            </h2>
            <p className="text-gray-700">
              In connection with the provision of the Services, Elektra Health,
              the Practice and/or any of the Practitioners may, from time to
              time, make a recommendation to you of a product or service from a
              third party unaffiliated with Elektra Health, the Practice and/or
              the Practitioner. Such third party product or service
              recommendation shall in no way be deemed an endorsement or
              guarantee by Elektra Health, the Practice and/or the Practitioner
              of such product or service and you agree to release and hold
              harmless Elektra Health, the Practice and/or the Practitioner from
              any and all claims you may have from the use of such third party
              product or service.
            </p>
          </section>

          {/* User Provided Content Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              User Provided Content
            </h2>
            <p className="mb-4 text-gray-700">
              Elektra Health may, in our sole discretion, permit you to post or
              submit content and other materials through the Services
              (collectively, "User-Provided Content"). You grant Elektra Health,
              and anyone authorized by us, a royalty-free, perpetual,
              irrevocable, non-exclusive, unrestricted, worldwide license to
              use, copy, modify, transmit, sell, exploit, create derivative
              works from, distribute, and/or publicly perform or display such
              User-Provided Content, in whole or in part, in any manner or
              medium (whether now known or hereafter developed), for the purpose
              of providing the Services. Also, you grant Elektra Health, and
              anyone authorized by Elektra Health, the right to identify you as
              the author of any User-Provided Content submitted by you. You will
              not receive any compensation of any kind for the use of any
              User-Provided Content submitted by you.
            </p>
            <p className="mb-4 text-gray-700">
              You acknowledge that Elektra Health only acts as a passive conduit
              for the distribution of the User-Provided Content and is not
              responsible or liable to you or to any third party for the content
              or accuracy of the User-Provided Content. You understand that
              Elektra Health has no obligation to monitor any areas of the
              Service through which users can post User-Provided Content.
              However, Elektra Health reserves the right at all times, in our
              sole discretion, to screen User-Provided Content and to edit,
              move, delete, and/or refuse to accept any User-Provided Content
              that in our judgment violates these TOU and/or the Privacy Policy
              or is otherwise unacceptable or inappropriate, whether for legal
              or other reasons.
            </p>
            <p className="text-gray-700">
              Any use by you of the User-Provided Content is entirely at your
              own risk. You represent and warrant that any User-Provided Content
              posted or transmitted by you is original to you and does not copy
              the work of any third party or otherwise infringe any third party
              intellectual property rights, rights of privacy or publicity
              rights and does not contain any defamatory or disparaging
              statements. Furthermore, you represent and warrant that you have
              the right to grant the license described above.
            </p>
          </section>

          {/* Unauthorized Use Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              Unauthorized Use
            </h2>
            <p className="mb-4 text-gray-700">
              Any use or attempted use of this Site: (1) for any unlawful,
              unauthorized, fraudulent or malicious purpose; (2) that could
              damage, disable, overburden, or impair the Site; (3) that could
              interfere with any other party's use and enjoyment of the Site;
              (4) to gain unauthorized access to any other accounts, computer
              systems, or networks connected to any server or systems through
              hacking, password mining or any other means; or (5) for any other
              use other than the business purpose of which it was intended, is
              prohibited. Elektra Health reserves the right to take whatever
              lawful actions it may deem appropriate in response to actual or
              suspected violations of the foregoing, including, without
              limitation, the suspension or termination of a user's access
              and/or account. Elektra Health further reserves the right to
              monitor general use of the Site at any time as it deems
              appropriate and to remove any materials that may violate these
              TOU.
            </p>
            <p className="mb-4 text-gray-700">
              In addition, in connection with your use of the Site, you agree
              you will not: (a) upload or transmit any message, information,
              data, text, software or images, or other content that is unlawful,
              harmful, threatening, abusive, harassing, tortious, defamatory,
              vulgar, obscene, libelous, or inappropriate with respect to race,
              gender, sexuality, ethnicity, or other intrinsic characteristic,
              or that may invade another's right of privacy or publicity; (b)
              create a false identity or duplicative accounts for the purpose of
              misleading others or impersonate any person or entity, including,
              without limitation, any Elektra Health representative, or falsely
              state or otherwise misrepresent your affiliation with a person or
              entity; (c) upload or transmit any material that you do not have a
              right to reproduce, display or transmit under any law or under
              contractual or fiduciary relationships (such as nondisclosure
              agreements); (d) upload files that contain viruses, trojan horses,
              worms, time bombs, cancel-bots, corrupted files, spyware or any
              other similar software or programs that may damage the operation
              of another's computer or property of another; (e) delete any
              author attributions, legal notices or proprietary designations or
              labels that you upload to any communication feature; (f) use the
              Site's communication features in a manner that adversely affects
              the availability of its resources to other users (e.g., excessive
              shouting, use of all caps, or flooding continuous posting of
              repetitive text); (g) upload or transmit any unsolicited
              advertising, promotional materials, "junk mail," "spam," "chain
              letters," "pyramid schemes," "phishing" or any other form of
              solicitation, commercial or otherwise; (h) violate any applicable
              local, state, national or international law; (i) upload or
              transmit any material that infringes any patent, trademark,
              service mark, trade secret, copyright or other proprietary rights
              of any party; (j) delete or revise any material posted by any
              other person or entity; (k) manipulate or otherwise display the
              Site by using framing, mirroring or similar navigational
              technology; (l) probe, scan, test the vulnerability of or breach
              the authentication measures of, the Site or any related networks
              or systems; (m) register, subscribe, attempt to register, attempt
              to subscribe, unsubscribe, or attempt to unsubscribe, any party
              for any services or any contests, promotions or sweepstakes if you
              are not expressly authorized by such party to do so; (n) harvest
              or otherwise collect information about others, including email
              addresses; (o) use any robot, spider, scraper, or other automated
              or manual means to access the Site, or copy, download, distribute
              or reproduce any content or information on the Site; or (p) assist
              or permit any person in engaging in any of these activities; or
              (q) record, screenshot, nor distribute any Services or part of the
              Elektra Health site or events or interactions.
            </p>
            <p className="text-gray-700">
              Elektra Health may terminate your use of the Site or any of our
              features or services at any time and for any reason without
              notice. You agree that if your use of the Site is terminated
              pursuant to these TOU, you will not attempt to use the Site under
              any name, real or assumed. Except as otherwise provided in the
              Privacy Policy, or as required by applicable law, we have no
              obligation, whether before or after the termination of your use of
              the Site, to return or otherwise provide to you any information to
              us, or any other information that we have that relates to you.
            </p>
          </section>

          {/* Indemnification Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              Indemnification
            </h2>
            <p className="text-gray-700">
              You agree to indemnify and hold harmless Elektra Health, its
              officers, directors, employees, agents, developers, vendors,
              affiliates, third party information providers, licensors and
              others involved in the development or the delivery of products,
              services or information over this Site, from and against any and
              all liabilities, expenses, damages and costs, including reasonable
              attorneys' fees, arising from any violation by you of these TOU or
              your use of the Site or any products, services or information
              obtained from the Site.
            </p>
          </section>

          {/* Disclaimers Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              Disclaimers
            </h2>
            <p className="text-gray-700">
              ANY ACCESS OR USE OF THE SITE IS VOLUNTARY AND AT THE SOLE RISK OF
              THE USER. ELEKTRA HEALTH DISCLAIMS ALL WARRANTIES AND CONDITIONS
              OF ANY KIND, EITHER EXPRESS OR IMPLIED, STATUTORY OR OTHERWISE,
              INCLUDING, WITHOUT LIMITATION, THE IMPLIED WARRANTIES OF
              MERCHANTABILITY, NON-INFRINGEMENT OF THIRD PARTIES' RIGHTS,
              SATISFACTORY QUALITY AND FITNESS FOR PARTICULAR PURPOSE WITH
              REGARD TO THE SITE, AND WITH RESPECT TO ANY INFORMATION, CONTENT,
              PRODUCT, SERVICE, MERCHANDISE, OR OTHER MATERIAL PROVIDED ON OR
              THROUGH THE SITE. ELEKTRA HEALTH DOES NOT WARRANT OR GUARANTEE THE
              ACCURACY, COMPLETENESS, RELIABILITY, TIMELINESS OR USEFULNESS OF
              THE SITE. ELEKTRA HEALTH DOES NOT WARRANT THAT THE SITE WILL
              FUNCTION WITHOUT DELAYS, DISRUPTIONS, INTERFERENCES, OR
              IMPERFECTIONS.
            </p>
          </section>

          {/* Limitation of Liability Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              Limitation of Liability
            </h2>
            <p className="mb-4 text-gray-700">
              IN NO EVENT SHALL ELEKTRA HEALTH, ITS OFFICERS, DIRECTORS,
              EMPLOYEES, OR ITS SUPPLIERS BE LIABLE FOR ANY SPECIAL, DIRECT,
              INDIRECT, PUNITIVE, INCIDENTAL, EXEMPLARY OR CONSEQUENTIAL
              DAMAGES, OR ANY DAMAGES WHATSOEVER ARISING OUT OF OR RESULTING
              FROM ACCESS TO, THE USE OR LOSS/INABILITY OF USE OF THIS SITE OR
              ANY OTHER SITE LINKED TO THIS SITE, UNAUTHORIZED ACCESS TO OR
              ALTERATION OF YOUR TRANSMISSIONS OR DATA, STATEMENT OR CONDUCT OF
              ANY THIRD PARTY, ANY INFORMATION, DATA OR OTHER MATERIAL
              TRANSMITTED THROUGH OR RESIDING ON THIS SITE, ERRORS OR OMISSIONS
              IN THE CONTENT HEREOF, OR ANY OTHER MATTER RELATING TO YOUR USE OF
              OR ACCESS TO THIS SITE. SOME STATE STATUTES MAY APPLY REGARDING
              LIMITATION OF LIABILITY.
            </p>
            <p className="mb-4 text-gray-700">
              TO THE EXTENT ANY ASPECTS OF THE FOREGOING LIMITATIONS OF
              LIABILITY ARE NOT ENFORCEABLE, THE MAXIMUM LIABILITY OF ELEKTRA
              HEALTH TO YOU WITH RESPECT TO YOUR USE OF THIS SITE IS $500 (FIVE
              HUNDRED DOLLARS).
            </p>
            <p className="text-gray-700">
              YOU HEREBY RELEASE AND FOREVER WAIVE AND DISCHARGE ANY AND ALL
              CLAIMS AND LIABILITIES YOU MAY HAVE AGAINST ELEKTRA HEALTH FROM
              ANY AND ALL MATTERS RELATING TO YOUR USE OF THIS SITE. IF YOU DO
              NOT AGREE TO THE TERMS AND CONDITIONS OR IF YOU SEEK TO ASSERT ANY
              CLAIM AGAINST ELEKTRA HEALTH ARISING OUT OF OR RELATING TO THE
              TERMS AND CONDITIONS OR YOUR USE OF THIS SITE, YOUR SOLE AND
              EXCLUSIVE REMEDY IS TO DISCONTINUE USING THIS SITE.
            </p>
          </section>

          {/* Binding Arbitration Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              Binding Arbitration / Class Waiver
            </h2>
            <p className="mb-4 text-gray-700">
              YOU EXPRESSLY AGREE THAT ANY LEGAL CLAIM, DISPUTE OR OTHER
              CONTROVERSY BETWEEN YOU AND ELEKTRA HEALTH ARISING OUT OF OR
              OTHERWISE RELATING IN ANY WAY TO ELEKTRA HEALTH, INCLUDING,
              WITHOUT LIMITATION, CONTROVERSIES RELATING TO THE APPLICABILITY,
              ENFORCEABILITY OR VALIDITY OF ANY PROVISION OF THESE TOU
              (COLLECTIVELY "DISPUTES"), SHALL BE RESOLVED IN CONFIDENTIAL
              BINDING ARBITRATION CONDUCTED BEFORE ONE COMMERCIAL ARBITRATOR
              FROM THE AMERICAN ARBITRATION ASSOCIATION ("AAA"), RATHER THAN IN
              A COURT, AS DESCRIBED HEREIN. THE ARBITRATION WILL BE GOVERNED BY
              THE AAA'S COMMERCIAL ARBITRATION RULES AND, IF THE ARBITRATOR
              DEEMS THEM APPLICABLE, THE SUPPLEMENTARY PROCEDURES FOR CONSUMER
              RELATED DISPUTES (COLLECTIVELY "RULES AND PROCEDURES"). YOU
              ACKNOWLEDGE THAT YOU ARE VOLUNTARILY AND KNOWINGLY FORFEITING YOUR
              RIGHT TO A TRIAL BY JURY AND TO OTHERWISE PROCEED IN A LAWSUIT IN
              STATE OR FEDERAL COURT, EXCEPT AS EXPRESSLY PROVIDED HEREIN.
            </p>
            <p className="mb-4 text-gray-700">
              Arbitration is more informal than a lawsuit in court. Arbitration
              uses a neutral arbitrator instead of a judge or jury, and court
              review of an arbitration award is very limited. However, an
              arbitrator can award the same damages and relief on an individual
              basis that a court can award to an individual.
            </p>
            <p className="mb-4 text-gray-700">
              Payment of arbitration costs will be governed by the AAA's fee
              schedule. Each party agrees to pay its own attorneys' fees and
              expenses unless there is a governing statutory provision that
              requires the prevailing party to be paid attorneys' fees and
              expenses.
            </p>
            <p className="mb-4 text-gray-700">
              The arbitration shall be conducted in New York County, New York.
              The arbitrator's award shall be final and binding on all parties
              and may be entered as a judgment in any court of competent
              jurisdiction.
            </p>
            <p className="mb-4 text-gray-700">
              For more information on AAA, its Rules and Procedures, and how to
              file an arbitration claim, you may call AAA at 800-778-7879 or
              visit the AAA website at www.adr.org.
            </p>
            <p className="mb-4 text-gray-700">
              Notwithstanding anything to the contrary herein, to the extent the
              Dispute arises from: (a) a violation of either party's
              intellectual property rights in any manner; (b) any claim related
              to, or arising from, allegations of theft, piracy, unauthorized
              use or a violation of the Computer Fraud and Abuse Act; and/or (c)
              any claim for equitable relief; then you and Elektra Health agree
              that a party may seek injunctive remedies (or an equivalent type
              of urgent legal relief) in a state or federal court in New York
              County, New York, and both parties agree to submit to the personal
              jurisdiction of such courts in connection with such proceedings.
              In addition to the foregoing, either party may assert an
              individual action in small claims court for Disputes that are
              within the scope of such court's jurisdiction in lieu of
              arbitration as long as such action remains in such court and
              advances only on an individual (non-class, non-representative)
              basis.
            </p>
            <p className="mb-4 text-gray-700">
              ALL DISPUTES SUBJECT TO ARBITRATION UNDER THIS AGREEMENT MUST BE
              ARBITRATED ON AN INDIVIDUAL BASIS AND NOT ON A CLASS, COLLECTIVE
              OR REPRESENTATIVE BASIS. NO PARTY MAY BRING ANY CLAIM SUBJECT TO
              ARBITRATION PURSUANT TO THIS AGREEMENT AS A PRIVATE ATTORNEY
              GENERAL, IN A REPRESENTATIVE CAPACITY, OR AS A PLAINTIFF OR CLASS
              MEMBER IN ANY PURPORTED CLASS PROCEEDING. THE CLAIMS OF MORE THAN
              ONE USER CANNOT BE JOINED OR CONSOLIDATED WITH THOSE OF ANY OTHER
              USER. NO ARBITRATION SHALL BE CONSOLIDATED OR JOINED WITH ANY
              OTHER ARBITRATION. THE ARBITRATOR MAY AWARD RELIEF (INCLUDING
              MONETARY, INJUNCTIVE, AND DECLARATORY RELIEF) ONLY IN FAVOR OF THE
              INDIVIDUAL PARTY SEEKING RELIEF AND ONLY TO THE EXTENT NECESSARY
              TO PROVIDE RELIEF NECESSITATED BY THAT PARTY'S INDIVIDUAL
              CLAIM(S).
            </p>
            <p className="text-gray-700">
              If a decision is issued stating that applicable law precludes
              enforcement of any of this paragraph's limitations as to a
              particular claim or request for relief, then such claim or request
              for relief (and only that claim or request for relief) shall be
              severed from the arbitration and may be brought exclusively in the
              state or federal courts located in New York County, New York,
              subject to the parties' respective rights to appeal the decision.
              All other claims or requests for relief shall be arbitrated. The
              parties agree that any claims or requests for relief that are
              severed from an arbitration may not proceed in litigation and
              shall be stayed until all claims between the parties remaining in
              arbitration are finally resolved. The parties agree to submit to
              the personal jurisdiction of the federal and state courts located
              in New York County, New York for purposes of resolving any claims
              or requests for relief severed from arbitration pursuant to this
              paragraph.
            </p>
          </section>

          {/* Governing Law Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              Governing Law and Jurisdiction
            </h2>
            <p className="text-gray-700">
              Accessing this Site or use of any of its content or services from
              locations where such access or use of its content or services is
              illegal is prohibited. Notwithstanding the laws of the location
              from which you access this Site, you agree that the statutes and
              laws of the State of New York, notwithstanding any principles or
              conflicts of law, will apply to all matters relating to the use of
              this Site. If any part of these Terms and Conditions are deemed
              unlawful, void, or unenforceable, that part will be deemed
              severable and will not affect the validity and enforceability of
              any remaining provisions.
            </p>
          </section>

          {/* Entire Agreement Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              Entire Agreement
            </h2>
            <p className="text-gray-700">
              These TOU and any other agreements Elektra Health may post on the
              Site or that you and Elektra Health may execute from time to time
              constitute the entire agreement amongst Elektra Health, the
              Practice, the Practitioners and you in connection with your use of
              the Site and accessing the Services and supersede any prior
              agreements, including prior versions of these TOU.
            </p>
          </section>

          {/* Changes Section */}
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              Changes to these TOU
            </h2>
            <p className="text-gray-700">
              When we make changes, we will revise the "last modified" date at
              the bottom of this document. We encourage you to review these TOU
              periodically. Your continued use of Elektra Health constitutes
              your agreement to the changed TOU.
            </p>
          </section>
        </div>

        <div className="mt-8 rounded-lg bg-white p-6 text-center">
          <p className="mb-2 text-sm text-gray-500">
            By using Elektra Health's services, you acknowledge that you have
            read and understood these Terms of Use and agree to be bound by
            them.
          </p>
          <p className="text-sm text-gray-500">
            <strong>Last modified:</strong> August 22, 2025
          </p>
        </div>
      </div>
    </main>
  );
};
